import {
  Box,
  Button,
  Collapse,
  Container,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import SereneCartButton from '@components/cart/serene-cart-button'
import { useModalAction } from '@components/common/modal/modal.context'
import Search from '@components/common/search'
import SearchIcon from '@components/icons/search-icon'
import UserIcon from '@components/icons/user-icon'
import CurrencySwitcher from '@components/ui/currency-switcher'
import Logo from '@components/ui/logo'
import { useStore } from '@contexts/store'
import { useAnalyticsEvents } from '@hooks/use-analytics-events'
import { useCurrencySwitcher } from '@hooks/use-currency-switcher'
import { singleCurrencyObj } from '@interfaces/currencies'
import { ROUTES } from '@utils/routes'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { RxHamburgerMenu } from 'react-icons/rx'

const SereneHeader = () => {
  const { openModal } = useModalAction()
  const { isOpen, onToggle } = useDisclosure()
  const {
    storeData: shop,
    isAuthorized,
    toggleMobileSearch,
    displayMobileSearch,
    logout,
  } = useStore()
  const router = useRouter()

  const { selectedCurrency, handleItemClick } = useCurrencySwitcher()

  const { trackContactUsClick } = useAnalyticsEvents()

  const showMobileMenuOptions = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    {
      fallback: 'md',
    },
  )

  const headerMenu = [
    {
      text: 'About',
      link: ROUTES.ABOUT_US,
    },
    {
      text: 'Contact',
      link: ROUTES.CONTACT_US,
      extraAction: trackContactUsClick,
    },
    {
      text: 'Policies',
      link: ROUTES.STORE_POLICIES,
    },
    {
      text: 'Find my order',
      action: () => openModal('FIND_MY_ORDER'),
    },

    ...(showMobileMenuOptions
      ? [
          {
            text: 'Search store',
            action: toggleMobileSearch,
          },

          ...(isAuthorized
            ? [
                {
                  text: 'My Account',
                  action: () => router.push(ROUTES.ACCOUNT),
                },
                {
                  text: 'Logout',
                  action: logout,
                },
              ]
            : [
                {
                  text: 'Login',
                  action: () => openModal('LOGIN_VIEW'),
                },
              ]),
        ]
      : []),
  ]

  const headerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = headerRef?.current
    const listener = () => {
      if (window.scrollY > 300) {
        element?.classList.add('header-effect')
      } 
      if (window.scrollY < 250) {
        element?.classList.remove('header-effect')
      }
    }
    document.addEventListener('scroll', listener)
    return () => {
      document.removeEventListener('scroll', listener)
    }
  }, [])

  return (
    <>
      {displayMobileSearch ? (
        <Flex
          top="7%"
          position="fixed"
          zIndex="popover"
          left="0"
          right="0"
          justify="center"
          px="3"
        >
          <Search />
        </Flex>
      ) : null}
      <Box bg="white" zIndex="sticky" py="5" ref={headerRef}>
        <Container maxW="1200px" px="3">
          <Flex justify="space-between" align="center">
            <Logo />
            <HStack
              as="ul"
              spacing="3"
              listStyleType="none"
              display={{ base: 'none', md: 'flex' }}
            >
              {headerMenu.map(({ text, action, link, extraAction }) => (
                <Text
                  key={text}
                  as="li"
                  fontWeight="700"
                  fontSize="13px"
                  textTransform="uppercase"
                >
                  {link ? (
                    <Link href={link} onClick={extraAction}>
                      {text}
                    </Link>
                  ) : action ? (
                    <Text
                      as="span"
                      cursor="pointer"
                      onClick={() => {
                        action()
                      }}
                    >
                      {text}
                    </Text>
                  ) : null}
                </Text>
              ))}
            </HStack>
            <HStack spacing="3.5">
              <Box display={{ base: 'inline-block', md: 'none' }}>
                <Menu>
                  <MenuButton
                    as={Button}
                    bg="transparent"
                    fontWeight="600"
                    rightIcon={<FaChevronDown />}
                    py="0"
                    px="0"
                    _active={{ bg: 'transparent' }}
                    _focus={{ bg: 'transparent' }}
                  >
                    {selectedCurrency?.currency?.symbol}
                  </MenuButton>
                  <MenuList minW="auto" w="auto">
                    {shop?.currencies?.map(
                      (singleCurrency: singleCurrencyObj) => (
                        <MenuItem
                          onClick={() =>
                            handleItemClick(singleCurrency.currency._id)
                          }
                          key={singleCurrency.currency._id}
                          value={singleCurrency.currency._id}
                        >
                          {`${singleCurrency.currency.code} - ${singleCurrency.currency.symbol}`}
                        </MenuItem>
                      ),
                    )}
                  </MenuList>
                </Menu>
              </Box>
              <Box
                as={'button'}
                pb="1"
                pt="1"
                onClick={toggleMobileSearch}
              >
                <SearchIcon boxSize={'20px'} />
              </Box>
              <Box display={{ base: 'none', md: 'inline-block' }}>
                <CurrencySwitcher />
              </Box>
              <SereneCartButton />

              {isAuthorized ? (
                <>
                  <Menu>
                    <MenuButton
                      cursor="pointer"
                      display={{ base: 'none', md: 'inline-block' }}
                    >
                      <Box as={UserIcon} />
                    </MenuButton>
                    <MenuList zIndex={100}>
                      <MenuItem onClick={() => router.push(ROUTES.ACCOUNT)}>
                        My Account
                      </MenuItem>
                      <MenuItem onClick={logout}>Log Out</MenuItem>
                    </MenuList>
                  </Menu>
                </>
              ) : (
                <Text
                  fontSize="14px"
                  cursor="pointer"
                  display={{ base: 'none', md: 'inline-block' }}
                  onClick={() => openModal('LOGIN_VIEW')}
                >
                  Login
                </Text>
              )}
              <IconButton
                aria-label="menu-button"
                p="0"
                boxSize="auto"
                minW="auto"
                display={{ base: 'inline-block', md: 'none' }}
                bg="transparent"
                onClick={onToggle}
                _hover={{ bg: 'transparent' }}
                icon={<RxHamburgerMenu size={20} />}
              />
            </HStack>
          </Flex>
        </Container>
        <Collapse in={isOpen} animateOpacity>
          <Flex
            direction="column"
            as="ul"
            listStyleType="none"
            bg="white"
            mt="2"
            display={{ lg: 'none' }}
            boxShadow="md"
          >
            {headerMenu.map(({ text, action, link }) => (
              <Box
                bg="white"
                as="li"
                borderBottom="1px solid #ebebeb"
                key={text}
                textTransform="uppercase"
                fontSize="13px"
                fontWeight="bold"
              >
                {link ? (
                  <Box
                    as={Link}
                    w="full"
                    px="3"
                    py="3"
                    display="inline-block"
                    href={link}
                  >
                    {text}
                  </Box>
                ) : action ? (
                  <Text
                    as="span"
                    px="3"
                    py="3"
                    display="inline-block"
                    w="full"
                    cursor="pointer"
                    onClick={() => {
                      onToggle()
                      action()
                    }}
                  >
                    {text}
                  </Text>
                ) : null}
              </Box>
            ))}
          </Flex>
        </Collapse>
      </Box>
    </>
  )
}

export default SereneHeader
